import * as PIXI from 'pixi.js';
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  LANE_COUNT,
  LANE_WIDTH,
  LANE_CENTERS,
  PLAYER_WIDTH,
  PLAYER_HEIGHT,
  PLAYER_Y,
  OBSTACLE_WIDTH,
  OBSTACLE_HEIGHT,
  OBSTACLE_BASE_SPEED,
  OBSTACLE_SPEED_INCREMENT,
  OBSTACLE_MAX_SPEED,
  OBSTACLE_SPAWN_INTERVAL_MS,
  OBSTACLE_SPAWN_INTERVAL_MIN_MS,
  OBSTACLE_SPAWN_ACCELERATION,
  SCORE_PER_SECOND,
  ROAD_COLOR,
  LANE_LINE_COLOR,
  PLAYER_COLOR,
  OBSTACLE_COLORS,
  GRASS_COLOR,
} from './constants';
import type { Lane, ObstacleData } from './types';
import { SoundManager } from './SoundManager';
interface GameEngineCallbacks {
  onScoreUpdate: (score: number) => void;
  onGameOver: (finalScore: number) => void;
}
export class GameEngine {
  private app: PIXI.Application | null = null;
  private player!: PIXI.Graphics;
  private targetLane: Lane = 1;
  private obstacles: Map<string, { gfx: PIXI.Graphics; data: ObstacleData }> = new Map();
  private obstacleContainer!: PIXI.Container;
  private score = 0;
  private elapsedMs = 0;
  private lastSpawnMs = 0;
  private spawnInterval = OBSTACLE_SPAWN_INTERVAL_MS;
  private speed = OBSTACLE_BASE_SPEED;
  private running = false;
  private paused = false;
  private callbacks: GameEngineCallbacks;
  private obstacleIdCounter = 0;
  private playerX = LANE_CENTERS[1];
  private playerTargetX = LANE_CENTERS[1];
  private sounds: SoundManager;
  private destroyed = false;
  private initCompleted = false;
  private pendingStart = false;
  private boundTick: ((delta: PIXI.Ticker) => void) | null = null;
  private container: HTMLElement;
  constructor(container: HTMLElement, callbacks: GameEngineCallbacks) {
    this.callbacks = callbacks;
    this.sounds = new SoundManager();
    this.container = container;
    void this.init();
  }
  private async init() {
    if (this.destroyed) return;
    try {
      this.app = new PIXI.Application();
      await this.app.init({
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        backgroundColor: ROAD_COLOR,
        antialias: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        autoDensity: true,
      });
    } catch (err) {
      console.error('[GameEngine] Failed to initialize WebGL renderer:', err);
      return;
    }
    if (this.destroyed) {
      this.app?.destroy(false);
      this.app = null;
      return;
    }
    const canvas = this.app.canvas;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    canvas.style.touchAction = 'none';
    this.container.appendChild(canvas);
    this.initCompleted = true;
    this.buildScene();
    this.boundTick = this.tick.bind(this);
    this.app.ticker.add(this.boundTick);
    if (this.pendingStart) {
      this.pendingStart = false;
      this.start();
    }
  }
  private buildScene() {
    if (!this.app) return;
    const grassLeft = new PIXI.Graphics();
    grassLeft.rect(-40, 0, 40, CANVAS_HEIGHT).fill(GRASS_COLOR);
    this.app.stage.addChild(grassLeft);
    const grassRight = new PIXI.Graphics();
    grassRight.rect(CANVAS_WIDTH, 0, 40, CANVAS_HEIGHT).fill(GRASS_COLOR);
    this.app.stage.addChild(grassRight);
    for (let i = 1; i < LANE_COUNT; i++) {
      const line = new PIXI.Graphics();
      line.rect(LANE_WIDTH * i - 2, 0, 4, CANVAS_HEIGHT).fill(LANE_LINE_COLOR);
      this.app.stage.addChild(line);
    }
    this.obstacleContainer = new PIXI.Container();
    this.app.stage.addChild(this.obstacleContainer);
    this.player = new PIXI.Graphics();
    this.drawPlayer();
    this.player.x = this.playerX;
    this.player.y = PLAYER_Y;
    this.app.stage.addChild(this.player);
  }
  private drawPlayer() {
    this.player.clear();
    this.player
      .roundRect(-PLAYER_WIDTH / 2, -PLAYER_HEIGHT / 2, PLAYER_WIDTH, PLAYER_HEIGHT, 6)
      .fill(PLAYER_COLOR);
    this.player
      .roundRect(-PLAYER_WIDTH / 2 + 6, -PLAYER_HEIGHT / 2 + 8, PLAYER_WIDTH - 12, PLAYER_HEIGHT * 0.35, 3)
      .fill(0x003344);
    const ww = 8, wh = 14;
    this.player.rect(-PLAYER_WIDTH / 2 - ww + 2, -PLAYER_HEIGHT / 2 + 4, ww, wh).fill(0x111111);
    this.player.rect(PLAYER_WIDTH / 2 - 2, -PLAYER_HEIGHT / 2 + 4, ww, wh).fill(0x111111);
    this.player.rect(-PLAYER_WIDTH / 2 - ww + 2, PLAYER_HEIGHT / 2 - 4 - wh, ww, wh).fill(0x111111);
    this.player.rect(PLAYER_WIDTH / 2 - 2, PLAYER_HEIGHT / 2 - 4 - wh, ww, wh).fill(0x111111);
  }
  private spawnObstacle() {
    const lane = Math.floor(Math.random() * LANE_COUNT) as Lane;
    const id = `obs_${this.obstacleIdCounter++}`;
    const color = OBSTACLE_COLORS[Math.floor(Math.random() * OBSTACLE_COLORS.length)];
    const gfx = new PIXI.Graphics();
    gfx.roundRect(-OBSTACLE_WIDTH / 2, -OBSTACLE_HEIGHT / 2, OBSTACLE_WIDTH, OBSTACLE_HEIGHT, 6).fill(color);
    gfx.roundRect(-OBSTACLE_WIDTH / 2 + 6, -OBSTACLE_HEIGHT / 2 + 6, OBSTACLE_WIDTH - 12, OBSTACLE_HEIGHT * 0.3, 3).fill(0x330000);
    const ww = 7, wh = 12;
    gfx.rect(-OBSTACLE_WIDTH / 2 - ww + 2, -OBSTACLE_HEIGHT / 2 + 4, ww, wh).fill(0x111111);
    gfx.rect(OBSTACLE_WIDTH / 2 - 2, -OBSTACLE_HEIGHT / 2 + 4, ww, wh).fill(0x111111);
    gfx.rect(-OBSTACLE_WIDTH / 2 - ww + 2, OBSTACLE_HEIGHT / 2 - 4 - wh, ww, wh).fill(0x111111);
    gfx.rect(OBSTACLE_WIDTH / 2 - 2, OBSTACLE_HEIGHT / 2 - 4 - wh, ww, wh).fill(0x111111);
    const data: ObstacleData = {
      id,
      lane,
      y: -OBSTACLE_HEIGHT,
      width: OBSTACLE_WIDTH,
      height: OBSTACLE_HEIGHT,
      speed: this.speed,
    };
    gfx.x = LANE_CENTERS[lane];
    gfx.y = data.y;
    this.obstacleContainer.addChild(gfx);
    this.obstacles.set(id, { gfx, data });
  }
  private checkCollision(): boolean {
    const px = this.playerX;
    const py = PLAYER_Y;
    const pw = PLAYER_WIDTH * 0.8;
    const ph = PLAYER_HEIGHT * 0.8;
    for (const { data } of this.obstacles.values()) {
      const ox = LANE_CENTERS[data.lane];
      const oy = data.y;
      const ow = data.width * 0.75;
      const oh = data.height * 0.75;
      if (
        Math.abs(px - ox) < (pw + ow) / 2 &&
        Math.abs(py - oy) < (ph + oh) / 2
      ) {
        return true;
      }
    }
    return false;
  }
  private tick(ticker: PIXI.Ticker) {
    if (!this.running || this.paused || this.destroyed) return;
    const delta = ticker.deltaMS;
    this.elapsedMs += delta;
    this.speed = Math.min(
      OBSTACLE_MAX_SPEED,
      OBSTACLE_BASE_SPEED + this.elapsedMs * OBSTACLE_SPEED_INCREMENT
    );
    this.score += (SCORE_PER_SECOND * delta) / 1000;
    this.callbacks.onScoreUpdate(Math.floor(this.score));
    if (this.elapsedMs - this.lastSpawnMs > this.spawnInterval) {
      this.spawnObstacle();
      this.lastSpawnMs = this.elapsedMs;
      this.spawnInterval = Math.max(
        OBSTACLE_SPAWN_INTERVAL_MIN_MS,
        this.spawnInterval * OBSTACLE_SPAWN_ACCELERATION
      );
    }
    const targetX = LANE_CENTERS[this.targetLane];
    this.playerTargetX = targetX;
    this.playerX += (this.playerTargetX - this.playerX) * 0.18;
    if (Math.abs(this.playerX - targetX) < 0.5) {
      this.playerX = targetX;
    }
    this.player.x = this.playerX;
    this.player.y = PLAYER_Y;
    const toRemove: string[] = [];
    for (const [id, { gfx, data }] of this.obstacles.entries()) {
      data.y += data.speed * (delta / 16);
      gfx.y = data.y;
      if (data.y > CANVAS_HEIGHT + OBSTACLE_HEIGHT) {
        toRemove.push(id);
      }
    }
    for (const id of toRemove) {
      const entry = this.obstacles.get(id);
      if (entry) {
        this.obstacleContainer.removeChild(entry.gfx);
        entry.gfx.destroy();
      }
      this.obstacles.delete(id);
    }
    if (this.checkCollision()) {
      this.running = false;
      this.sounds.playCollision();
      this.callbacks.onGameOver(Math.floor(this.score));
    }
  }
  switchLane(direction: -1 | 1) {
    if (!this.running || this.paused) return;
    const next = this.targetLane + direction;
    if (next < 0 || next >= LANE_COUNT) return;
    this.targetLane = next as Lane;
    this.sounds.playSwipe();
  }
  pause() { this.paused = true; }
  resume() { this.paused = false; }
  togglePause() { this.paused = !this.paused; }
  getIsPaused() { return this.paused; }
  start() {
    if (this.destroyed) return;
    if (!this.initCompleted) {
      this.pendingStart = true;
      this.score = 0;
      this.elapsedMs = 0;
      this.lastSpawnMs = 0;
      this.spawnInterval = OBSTACLE_SPAWN_INTERVAL_MS;
      this.speed = OBSTACLE_BASE_SPEED;
      this.targetLane = 1;
      this.playerX = LANE_CENTERS[1];
      this.playerTargetX = LANE_CENTERS[1];
      this.obstacleIdCounter = 0;
      this.paused = false;
      return;
    }
    this.score = 0;
    this.elapsedMs = 0;
    this.lastSpawnMs = 0;
    this.spawnInterval = OBSTACLE_SPAWN_INTERVAL_MS;
    this.speed = OBSTACLE_BASE_SPEED;
    this.targetLane = 1;
    this.playerX = LANE_CENTERS[1];
    this.playerTargetX = LANE_CENTERS[1];
    this.obstacleIdCounter = 0;
    this.paused = false;
    for (const { gfx } of this.obstacles.values()) {
      this.obstacleContainer.removeChild(gfx);
      gfx.destroy();
    }
    this.obstacles.clear();
    this.running = true;
    this.sounds.startMusic();
  }
  stop() {
    this.running = false;
    this.sounds.stopMusic();
  }
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.running = false;
    if (!this.initCompleted) return;
    try {
      if (this.boundTick && this.app) {
        this.app.ticker.remove(this.boundTick);
      }
      for (const { gfx } of this.obstacles.values()) {
        gfx.destroy();
      }
      this.obstacles.clear();
      this.sounds.destroy();
      if (this.app) {
        this.app.renderer.events.setTargetElement(null);
        const canvas = this.app.canvas;
        if (canvas.parentElement) {
          canvas.parentElement.removeChild(canvas);
        }
        this.app.destroy(false);
        this.app = null;
      }
    } catch {
      // ignore cleanup errors
    }
  }
  getIsRunning() { return this.running; }
}